import React, { useState } from "react";
import { useGetAiResistantHomework } from "@packages/api/madlenAPI";
import { Expand, X } from "lucide-react";
import Markdown from "react-markdown";
import { getComplexityColor } from "@features/tools/components/ai-resistant-homework-ideas/AiHomeworkIdeaCard";
import { useTranslation } from "react-i18next";
import { AiHomeworkIdeaCard } from "@features/tools/components/ai-resistant-homework-ideas/AiHomeworkIdeaCard";
import Text from "@core/text";
import { Button } from "@core/index";
import { AiResistantHomeworkResponseDto } from "@packages/api/madlenAPI.schemas";
import SpinnerLoader from "../components/SpinnerLoader";

const AiResistantHomeworkDetailComponent = ({
  data,
  isImageLoading,
  closeModal,
}: {
  data: AiResistantHomeworkResponseDto;
  isImageLoading: boolean;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 dark:bg-opacity-90 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <Text variant={"h1"}>{t("aiHomeworkIdeas.mainTitle")}</Text>
          <Button
            onClick={closeModal}
            variant="solid"
            size="icon"
            rounded="full"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={24} className="text-gray-500 dark:text-gray-400" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-6 modal-content">
          <AiHomeworkIdeaCard
            homeworkIdea={{
              ...data,
              imageUrl: data.imageUrl,
              videoUrl: data.videoUrl,
              createdAt: data.createdAt,
              ideas: data.reportIdeas,
              gradeLevel: data.gradeLevel,
              title: data.title || t("aiHomeworkIdeas.mainTitle"),
            }}
            isImageLoading={isImageLoading}
          />
        </div>
      </div>
    </div>
  );
};

interface AiResistantHomeworkProps {
  id: number | null;
}

const AiResistantHomework: React.FC<AiResistantHomeworkProps> = ({ id }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError } = useGetAiResistantHomework(id, {
    query: {
      refetchInterval: (data) => {
        if (
          data?.state.data?.reportIdeas &&
          data.state.data?.reportIdeas?.length > 0
        ) {
          return false;
        }
        return 2000;
      },
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30,
    },
  });

  const isContentLoading =
    isLoading || !data?.reportIdeas || data.reportIdeas.length === 0;

  const handleExpandClick = () => {
    setExpanded(true);
  };

  const closeModal = () => {
    setExpanded(false);
  };

  if (isContentLoading) {
    return <SpinnerLoader />;
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <h3 className="text-xl font-medium mb-4 text-red-500 dark:text-red-400">
          {t("aiHomeworkIdeas.error.unableToLoad")}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("aiHomeworkIdeas.error.tryRegenerate")}
        </p>
      </div>
    );
  }

  return (
    <>
      {expanded && data && (
        <AiResistantHomeworkDetailComponent
          data={{
            ...data,
            gradeLevel: data.gradeLevel === -1 ? 0 : data.gradeLevel,
          }}
          isImageLoading={isLoading}
          closeModal={closeModal}
        />
      )}

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-medium flex items-center text-gray-900 dark:text-gray-100">
            {t("aiHomeworkIdeas.mainTitle")}
          </h3>
          <button
            className="flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
            onClick={handleExpandClick}
          >
            <Expand size={18} className="text-gray-400 dark:text-gray-500" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("aiHomeworkIdeas.subtitle")}
        </p>

        <div className="space-y-1">
          {data.reportIdeas &&
            data.reportIdeas.slice(0, 2).map((idea, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-7 shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="space-y-4">
                  {idea.title && (
                    <div className="flex justify-between items-start mb-2">
                      <Markdown className="prose dark:prose-invert font-semibold">
                        {idea.title}
                      </Markdown>
                      {/* Use dynamic complexity level colors */}
                      {idea.complexity_level && (
                        <span
                          className={`px-3 py-1 text-xs font-medium rounded-full ${getComplexityColor(idea.complexity_level).bg} ${getComplexityColor(idea.complexity_level).text} ml-3`}
                        >
                          <Markdown className="prose dark:prose-invert font-semibold">
                            {t(
                              `aiHomeworkIdeas.complexity.${idea.complexity_level}`,
                            )}
                          </Markdown>
                        </span>
                      )}
                    </div>
                  )}

                  {idea.description && (
                    <div>
                      <h4 className="text-lg font-semibold text-teal-700 dark:text-teal-300 mb-2">
                        {t("aiHomeworkIdeas.assignmentDescription")}
                      </h4>
                      <div
                        className={`pl-4 border-l-4 ${
                          idea.complexity_level
                            ? getComplexityColor(idea.complexity_level).border
                            : "border-gray-300 dark:border-gray-600"
                        } pt-1 pb-1`}
                      >
                        <Markdown className="prose dark:prose-invert max-w-none whitespace-pre-line">
                          {idea.description.length > 200
                            ? `${idea.description.substring(0, 200)}...`
                            : idea.description}
                        </Markdown>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </>
  );
};

export default AiResistantHomework;
